<template>
    <div class="ui-core-layout" :class="layoutClasses" :style="styleVars">
        <picture class="bg-image" v-if="hasBackgroundImage">
            <source
                v-if="blok.background?.[0]?.backgroundMobileImage?.filename"
                :srcset="blok.background?.[0]?.backgroundMobileImage?.filename"
                media="(max-width: 767px)"
                type="image/webp"
            />
            <source
                v-if="blok.background?.[0]?.backgroundTabletImage?.filename"
                :srcset="blok.background?.[0]?.backgroundTabletImage?.filename"
                media="(min-width: 768px) and (max-width: 1023px)"
                type="image/webp"
            />
            <NuxtImg
                :src="blok.background?.[0]?.backgroundImage?.filename"
                :alt="blok.background?.[0]?.backgroundImage?.alt || ''"
                provider="storyblok"
                format="webp"
                quality="75"
                class="bg-img"
                loading="lazy"
            />
        </picture>
        <div
            class="mx-auto content-container"
            :class="{ container: !blok.contentFullWidth }"
            :style="!blok.contentFullWidth && !blok.background?.[0]?.hasBackgroundImage ? background : {}"
        >
            <MobileCarousel v-if="isMobileView && blok.mobileBehavior === EMobileBehaviors.CAROUSEL" :blok="blok" />
            <Column v-else :blok="blok" />
        </div>
    </div>
</template>

<script setup lang="ts">
import type { ComputedRef, CSSProperties } from 'vue';
import { buildBackground } from '@nuxt-monorepo/ui/utils/styleBuilder';
import type { ICoreLayout } from '@nuxt-monorepo/ui/types/components/CoreLayout';
import { useIsMobileView } from '@nuxt-monorepo/ui/composables/isMobileView';
import screens from '#tailwind-config/theme/screens';
import Column from '@nuxt-monorepo/ui/components/Core/Layout/Column.vue';
import MobileCarousel from '@nuxt-monorepo/ui/components/Core/Layout/MobileCarousel.vue';
import { EHideOnType, EMobileBehaviors } from '@nuxt-monorepo/ui/types/enums/ComponentStyle';

const { blok } = defineProps<{ blok: ICoreLayout }>();
const { isMobileView } = useIsMobileView(screens.md);

const background: CSSProperties = buildBackground(blok.background?.[0] ?? {}) as CSSProperties;

const layoutStyles: ComputedRef<CSSProperties> = computed(() => ({
    ...(blok.bgFullWidth && !blok.background?.[0]?.hasBackgroundImage ? background : {}),
    paddingTop: blok.paddingTop ? `${blok.paddingTop}px` : undefined,
    paddingRight: blok.paddingRight ? `${blok.paddingRight}px` : undefined,
    paddingLeft: blok.paddingLeft ? `${blok.paddingLeft}px` : undefined,
    paddingBottom: blok.paddingBottom ? `${blok.paddingBottom}px` : undefined,
}));

const layoutClasses = computed(() => ({
    divider: !!blok.hasDivider,
    'hide-mobile': blok.hideOn === EHideOnType.Mobile,
    'hide-desktop': blok.hideOn === EHideOnType.Desktop,
}));

const hasBackgroundImage = computed(() => {
    return Boolean(blok.background?.[0]?.hasBackgroundImage && blok.background?.[0]?.backgroundImage?.filename);
});

const styleVars = computed(() => {
    if (!hasBackgroundImage.value) return layoutStyles.value;

    return {
        '--desktop-fit': normalizeFit(blok.background?.[0]?.backgroundImageFit) || 'cover',
        '--desktop-position': blok.background?.[0]?.backgroundImagePosition || 'center',
        '--tablet-fit':
            normalizeFit(blok.background?.[0]?.backgroundTabletImageFit) ||
            normalizeFit(blok.background?.[0]?.backgroundImageFit) ||
            'cover',
        '--tablet-position':
            blok.background?.[0]?.backgroundTabletImagePosition ||
            blok.background?.[0]?.backgroundImagePosition ||
            'center',
        '--mobile-fit':
            normalizeFit(blok.background?.[0]?.backgroundMobileImageFit) ||
            normalizeFit(blok.background?.[0]?.backgroundImageFit) ||
            'cover',
        '--mobile-position':
            blok.background?.[0]?.backgroundMobileImagePosition ||
            blok.background?.[0]?.backgroundImagePosition ||
            'center',
        ...layoutStyles.value,
    };
});

// Normalizes value to have similar behavior to live site
const normalizeFit = (value?: string): string => {
    if (!value) {
        return 'cover';
    }

    const mapping: Record<string, string> = {
        'contain no-repeat': 'contain',
        contain: 'cover',
    };

    return mapping[value] ?? value;
};
</script>

<style scoped lang="postcss">
.container {
    @apply mx-auto px-4;
}

.ui-core-layout {
    @apply relative py-6 max-md:py-4 max-w-full;

    .bg-image {
        @apply absolute top-0 left-0 w-full h-full;

        .bg-img {
            width: 100%;
            height: 100%;
            object-fit: var(--desktop-fit);
            object-position: var(--desktop-position);

            @media (max-width: theme('screens.lg')) {
                object-fit: var(--tablet-fit);
                object-position: var(--tablet-position);
            }
            @media (max-width: theme('screens.md')) {
                object-fit: var(--mobile-fit);
                object-position: var(--mobile-position);
            }
        }
    }

    .content-container {
        @apply relative;
    }

    &.divider {
        border-top: 1px solid #e5e7eb;
        border-bottom: 1px solid #e5e7eb;
    }

    &.hide-desktop {
        @apply md:hidden;
    }

    &.hide-mobile {
        @apply max-md:hidden;
    }
}
</style>
